import type { DraftAccount } from '@interfaces/account';
import { AccountStates, AccountTypes } from '@interfaces/account';
import type { Affiliation } from '@interfaces/affiliation';
import type { AffiliationRelations } from '@interfaces/affiliationRelations';
import { JointAccountHolderRelations } from '@interfaces/affiliationRelations';
import { AffiliationTypes } from '@interfaces/affiliationTypes';
import type { Goal } from '@interfaces/goalSubAccountTypes';
import type { User } from '@interfaces/user';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Icon } from '@ovComponents/1-primative/icon/icon';
import { ClientSelectField } from '@ovComponents/2-component/clientSelectField/clientSelect';
import { Form } from '@ovComponents/2-component/form/form';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { BeneficiariesCrud } from '@ovComponents/3-pattern/beneficiaryCrud/beneficiariesCrud';
import {
  GoalLinkingInput,
  getValidGoals,
} from '@ovComponents/4-module/workflowCompletion/subSteps/account/goalLinking';
import {
  type FieldOptions,
  type FormErrors,
  validateFields,
} from '@ovComponents/4-module/workflowCompletion/subSteps/utils';
import {
  Box,
  Button,
  Checkbox,
  Grid,
  Typography,
  useTheme,
} from 'hephaestus/web';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type TSFADialogContentInterface = {
  userData: any;
  showGoalLinkingSection: boolean;
  onAccountCreated: (account: DraftAccount) => void;
  onDialogClose: () => void;
};

export const TSFADialogContent = ({
  userData,
  onAccountCreated,
  onDialogClose,
}: TSFADialogContentInterface) => {
  const { t } = useTranslation(['workflowCompletions', 'affiliationTypes']);
  const [successorEnabled, setSuccessorEnabled] = useState<boolean>(false);
  const [successor, setSuccessor] = useState<User>();
  const [successorRelationship, setSuccessorRelationship] =
    useState<string>('');
  const [beneficiariesEnabled, setBeneficiariesEnabled] =
    useState<boolean>(false);
  const [beneficiaries, setBeneficiaries] = useState<Affiliation[]>([]);
  const [goalsLinked, setGoalsLinked] = useState<Goal[]>([]);
  const goals = getValidGoals(userData);
  const [errors, setErrors] = useState<FormErrors>(null);
  const [touched, setTouched] = useState<string[]>([]);
  const theme = useTheme();

  const validate = useCallback(
    (candidateFields?: string[]): FormErrors => {
      const fieldOptions: FieldOptions = {
        successor: { required: successorEnabled },
        successorRelationship: { required: successorEnabled },
      };

      const data = {
        successor,
        successorRelationship,
      };
      let newErrors = validateFields(fieldOptions, data, candidateFields);

      if (
        beneficiariesEnabled &&
        (candidateFields?.includes('beneficiaries') ||
          candidateFields === undefined)
      ) {
        const totalAllocation = beneficiaries.reduce(
          (value, prev) => value + (prev.allocation ?? 0),
          0,
        );
        if (totalAllocation !== 100) {
          if (!newErrors) newErrors = {};
          newErrors.beneficiaries = { active: true };
        }
      }

      setErrors(newErrors);
      return newErrors;
    },
    [
      successorEnabled,
      successor,
      successorRelationship,
      beneficiariesEnabled,
      beneficiaries,
    ],
  );

  const submit = () => {
    const formErrors = validate();
    if (formErrors) {
      setTouched(Object.keys(formErrors));
      return;
    }

    const affiliations: Affiliation[] = [...beneficiaries];

    if (successor) {
      affiliations.push({
        relation: successorRelationship as AffiliationRelations,
        type: AffiliationTypes.Successor,
        user: successor,
      });
    }

    onAccountCreated({
      type: AccountTypes.TFSA,
      applyForGovFunds: [],
      id: '',
      state: AccountStates.ACTIVE,
      user: userData,
      affiliations,
      goalsLinked,
    });
  };

  useEffect(() => {
    validate(touched);
  }, [validate, touched]);

  useEffect(() => {
    if (beneficiaries.length > 0) {
      setTouched([...touched, 'beneficiaries']);
    }
  }, [beneficiaries]);

  /* reset conditional fields when checkbox is unchecked */
  useEffect(() => {
    if (successorEnabled === false) {
      setSuccessor(undefined);
      setSuccessorRelationship('');
    }
  }, [successorEnabled]);

  return (
    <Form onSubmit={submit}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'end',
        }}
      >
        <Box
          onClick={onDialogClose}
          sx={{
            display: 'flex',
            cursor: 'pointer',
          }}
        >
          <Icon icon={CloseRoundedIcon} size='medium' />
        </Box>
      </Box>
      <Typography variant='headingMedium' sx={{ mt: 1, mb: 2 }}>
        {t('workflowCompletions:tfsaAccountCreation.title')}
      </Typography>

      <Typography variant='bodySmall' component='p'>
        {t('workflowCompletions:tfsaAccountCreation.subtitle')} &nbsp;
      </Typography>

      {/* Add a successor */}
      <Box
        sx={{
          minHeight: '100px',
          mt: 3,
          mb: 1,
        }}
      >
        <HeadingWithCheckbox
          label={t('workflowCompletions:tfsaAccountCreation.addASuccessor')}
          checked={successorEnabled}
          onChange={setSuccessorEnabled}
        />
        <Grid
          container
          spacing={1}
          style={successorEnabled ? {} : { opacity: 0.5 }}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'end',
            my: 1,
          }}
        >
          <Grid size={7}>
            <ClientSelectField
              label={t('workflowCompletions:tfsaAccountCreation.successorName')}
              user={successor}
              setUser={(item) => setSuccessor(item)}
              fullWidth
              disabled={!successorEnabled}
              autoFocus
              onBlur={() => setTouched([...touched, 'successor'])}
              error={!!errors?.successor}
            />
          </Grid>
          <Grid size={5}>
            <SelectField
              onChange={(e: any) => setSuccessorRelationship(e.target.value)}
              label={t(
                'workflowCompletions:tfsaAccountCreation.relationshipToAccountHolder',
              )}
              fullWidth
              value={successorRelationship}
              onBlur={() => setTouched([...touched, 'successorRelationship'])}
              error={!!errors?.successorRelationship}
              disabled={!successorEnabled}
            >
              {JointAccountHolderRelations.map(
                (value: string, index: number) => (
                  <MenuItem value={value} key={index}>
                    {t(`affiliationTypes:affiliationRelationship.${value}`)}
                  </MenuItem>
                ),
              )}
            </SelectField>
          </Grid>
        </Grid>
      </Box>

      {/* Add beneficiaries */}
      <Box
        sx={{
          minHeight: '200px',
          my: 3,
        }}
      >
        <HeadingWithCheckbox
          label={t('workflowCompletions:tfsaAccountCreation.addBeneficiaries')}
          checked={beneficiariesEnabled}
          onChange={setBeneficiariesEnabled}
        />
        <Grid style={beneficiariesEnabled ? {} : { opacity: 0.5 }}>
          {errors?.beneficiaries && (
            <Typography
              variant='bodySmall'
              sx={{ color: theme.color.negative }}
            >
              {t('components:beneficiaryCrud.allocationError')}
            </Typography>
          )}
          <BeneficiariesCrud
            beneficiaries={beneficiaries}
            setBeneficiaries={setBeneficiaries}
            disabled={!beneficiariesEnabled}
          />
        </Grid>
      </Box>

      <Box sx={{ mt: 4 }}>
        <GoalLinkingInput
          goals={goals}
          goalsLinked={goalsLinked}
          setGoalsLinked={setGoalsLinked}
        />
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'end',
        }}
      >
        <Button color='primary' sx={{ mt: 3 }} type='submit'>
          {t('workflowCompletions:tfsaAccountCreation.addAccount')}
        </Button>
      </Box>
    </Form>
  );
};

export default TSFADialogContent;

type HeadingWithCheckboxProps = {
  checked?: boolean;
  disabled?: boolean;
  indeterminate?: boolean;
  label?: string;
  onChange?: (checked: boolean) => void;
  size?: 'medium' | 'small';
  testId?: string;
};

export const HeadingWithCheckbox = ({
  checked = false,
  disabled = false,
  indeterminate = false,
  label,
  onChange,
  size = 'medium',
  testId,
}: HeadingWithCheckboxProps) => {
  const checkboxTestId = testId ?? 'checkbox';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
      }}
    >
      <Checkbox
        checked={checked}
        slotProps={{
          root: { 'data-testid': checkboxTestId },
          input: { 'data-testid': `${checkboxTestId}-input` },
        }}
        disabled={disabled}
        indeterminate={indeterminate}
        onChange={(event) => onChange?.(event.target.checked)}
        size={size}
      />
      <Typography
        variant='headingXSmall'
        onClick={() => {
          if (!disabled) onChange?.(!checked);
        }}
        style={{ cursor: disabled ? 'default' : 'pointer', userSelect: 'none' }}
      >
        {label}
      </Typography>
    </Box>
  );
};
